// src/pages/Leaderboard.tsx
'use client';
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { getSupabase, currentUser, signOut } from "@/lib/supabase";
import { format, differenceInDays, formatDistanceToNow } from 'date-fns';
import Sidebar from "@/components/Sidebar";
import {
  Trophy, Settings, LogOut, ChevronLeft, Home, Apple, Dumbbell, 
  CalendarDays, Utensils, Users, Target, Share2, Plus, 
  Zap, Crown, Medal, Flame, TrendingUp, X, Check, Copy, UserPlus,
  Loader2, Clock, Play, Route, Menu
} from 'lucide-react';

// â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”
// GARMIN COMPATIBLE SPORTS
// â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”
const GARMIN_COMPATIBLE_SPORTS = ['running', 'swimming', 'cycling'];

function canUploadToGarmin(sportType: string): boolean {
  return GARMIN_COMPATIBLE_SPORTS.includes(sportType?.toLowerCase());
}

function Avatar({ src, name, size = 'md', className = '' }: {
  src?: string | null;
  name?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}) {
  const [imgError, setImgError] = useState(false);
  
  const getInitials = (name?: string): string => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) {
      return parts[0].substring(0, 2).toUpperCase();
    }
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const getColor = (name?: string): string => {
    if (!name) return 'bg-gray-600';
    const colors = [
      'bg-red-500', 'bg-orange-500', 'bg-amber-500', 'bg-yellow-500',
      'bg-lime-500', 'bg-green-500', 'bg-emerald-500', 'bg-teal-500',
      'bg-cyan-500', 'bg-sky-500', 'bg-blue-500', 'bg-indigo-500',
      'bg-violet-500', 'bg-purple-500', 'bg-fuchsia-500', 'bg-pink-500'
    ];
    const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  const sizeClasses = {
    sm: 'w-8 h-8 text-xs',
    md: 'w-10 h-10 text-sm',
    lg: 'w-12 h-12 text-base'
  };

  if (!src || imgError) {
    return (
      <div className={`${sizeClasses[size]} ${getColor(name)} rounded-full flex items-center justify-center font-bold text-[#0f172a] ${className}`}>
        {getInitials(name)}
      </div>
    );
  }

  return (
    <img 
      src={src} 
      alt={name || ''} 
      className={`${sizeClasses[size]} rounded-full object-cover ${className}`}
      onError={() => setImgError(true)}
    />
  );
}


// â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”
// HELPER: Call Edge Functions
// â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”
async function callEdgeFunction(functionName: string, body: object) {
  const supabase = getSupabase();
  const { data: { session } } = await supabase.auth.getSession();
  
  if (!session?.access_token) {
    throw new Error('Not authenticated');
  }

  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${functionName}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify(body),
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || 'Edge function failed');
  }

  return response.json();
}

// â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”
// TOAST NOTIFICATION (replace alerts)
// â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”
function Toast({ message, type, onClose }: { message: string; type: 'success' | 'error'; onClose: () => void }) {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-auto px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 z-50 ${
      type === 'success' ? 'bg-green-500 text-[#0f172a]' : 'bg-red-500 text-[#0f172a]'
    }`}>
      {type === 'success' ? <Check className="w-5 h-5 flex-shrink-0" /> : <X className="w-5 h-5 flex-shrink-0" />}
      <span className="text-sm">{message}</span>
    </div>
  );
}

// ============================================
// SHARED WORKOUTS TYPES
// ============================================

interface WorkoutStep {
  type: 'warmup' | 'work' | 'rest' | 'cooldown';
  duration: {
    type: 'distance' | 'time';
    value: number;
  };
  intensity: 'easy' | 'recovery' | 'steady' | 'tempo' | 'threshold' | '10km' | '5km' | 'half-marathon' | 'marathon';
  pace?: {
    value: number;
    unit: 'min_per_km';
    source: 'pb' | 'manual' | 'derived';
  };
  repeat?: number;
  description?: string;
}

interface WorkoutSegment {
  name: string;
  steps: WorkoutStep[];
}

interface WorkoutTemplate {
  id: string;
  name: string;
  description?: string;
  sport_type: string;
  workout_structure: { segments: WorkoutSegment[] };
  estimated_duration_minutes?: number;
  estimated_distance_meters?: number;
  difficulty?: string;
  share_code: string;
  created_at: string;
}

interface SharedSession {
  id: string;
  name: string;
  description?: string;
  session_code: string;
  starts_at?: string;
  ends_at?: string;
  is_active: boolean;
  workout_templates: WorkoutTemplate;
  participant_count?: number;
  my_status?: 'pending' | 'pushed' | 'completed';
}

// ============================================
// SHARED WORKOUTS COMPONENT
// ============================================

function SharedWorkouts() {
  const [workoutView, setWorkoutView] = useState<'sessions' | 'templates'>('sessions');
  const [sessions, setSessions] = useState<SharedSession[]>([]);
  const [templates, setTemplates] = useState<WorkoutTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [showCreateTemplate, setShowCreateTemplate] = useState(false);
  const [showCreateSession, setShowCreateSession] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<WorkoutTemplate | null>(null);

  useEffect(() => {
    loadWorkoutData();
  }, [workoutView]);

  const loadWorkoutData = async () => {
    setLoading(true);
    const user = await currentUser();
    if (!user) return;

    const supabase = getSupabase();

    if (workoutView === 'sessions') {
      const { data } = await supabase
        .from('shared_workout_participants')
        .select(`
          *,
          shared_workout_sessions(
            *,
            workout_templates(*)
          )
        `)
        .eq('user_id', user.id)
        .order('joined_at', { ascending: false });

      const sessionsData = data?.map(p => ({
        ...p.shared_workout_sessions,
        my_status: p.completed ? 'completed' : p.garmin_push_status
      })) || [];

      setSessions(sessionsData);
    } else if (workoutView === 'templates') {
      const { data } = await supabase
        .from('workout_templates')
        .select('*')
        .eq('creator_id', user.id)
        .order('created_at', { ascending: false });

      setTemplates(data || []);
    }

    setLoading(false);
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex gap-2 overflow-x-auto pb-2 sm:pb-0 -mx-4 px-4 sm:mx-0 sm:px-0">
          <button
            onClick={() => setWorkoutView('sessions')}
            className={`px-3 py-2 rounded-lg font-medium transition whitespace-nowrap text-sm ${
              workoutView === 'sessions' ? 'bg-green-500 text-[#0f172a]' : 'bg-[#f1f5f9] text-gray-500'
            }`}
          >
            <Users className="w-4 h-4 inline mr-1.5" />
            My Sessions
          </button>
          <button
            onClick={() => setWorkoutView('templates')}
            className={`px-3 py-2 rounded-lg font-medium transition whitespace-nowrap text-sm ${
              workoutView === 'templates' ? 'bg-green-500 text-[#0f172a]' : 'bg-[#f1f5f9] text-gray-500'
            }`}
          >
            <Dumbbell className="w-4 h-4 inline mr-1.5" />
            My Workouts
          </button>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => setShowJoinModal(true)}
            className="flex-1 sm:flex-none px-3 py-2 bg-blue-500 hover:bg-blue-400 text-[#0f172a] font-bold rounded-xl transition text-sm"
          >
            Join Session
          </button>
          <button
            onClick={() => setShowCreateTemplate(true)}
            className="flex-1 sm:flex-none px-3 py-2 bg-green-500 hover:bg-green-400 text-[#0f172a] font-bold rounded-xl transition text-sm"
          >
            <Plus className="w-4 h-4 inline mr-1" />
            Create
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-green-400" />
        </div>
      ) : (
        <>
          {/* Sessions View */}
          {workoutView === 'sessions' && (
            <div className="space-y-3">
              {sessions.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Dumbbell className="w-12 h-12 mx-auto mb-4 opacity-20" />
                  <p className="text-sm">No workout sessions yet</p>
                  <p className="text-xs mt-2">Join a session or create a workout to share!</p>
                </div>
              ) : (
                sessions.map(session => (
                  <WorkoutSessionCard 
                    key={session.id} 
                    session={session} 
                    onViewLeaderboard={() => {/* TODO */}}
                  />
                ))
              )}
            </div>
          )}

          {/* Templates View */}
          {workoutView === 'templates' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
              {templates.map(template => (
                <WorkoutTemplateCard 
                  key={template.id} 
                  template={template}
                  onShare={() => {
                    setSelectedTemplate(template);
                    setShowCreateSession(true);
                  }}
                />
              ))}
              
              {/* Create New Card */}
              <button
                onClick={() => setShowCreateTemplate(true)}
                className="bg-[#f8fafc] border-2 border-dashed border-[#e2e8f0] rounded-2xl p-6 hover:border-green-500/50 transition flex flex-col items-center justify-center gap-2 min-h-[160px]"
              >
                <Plus className="w-8 h-8 text-gray-500" />
                <span className="font-medium text-gray-500 text-sm">Create Workout</span>
              </button>
            </div>
          )}
        </>
      )}

      {/* Modals */}
      {showJoinModal && (
        <JoinWorkoutSessionModal onClose={() => setShowJoinModal(false)} onJoined={loadWorkoutData} />
      )}
      
      {showCreateTemplate && (
        <CreateWorkoutModal 
          onClose={() => setShowCreateTemplate(false)} 
          onCreated={() => {
            loadWorkoutData();
            setShowCreateTemplate(false);
          }}
        />
      )}

      {showCreateSession && selectedTemplate && (
        <CreateWorkoutSessionModal
          template={selectedTemplate}
          onClose={() => {
            setShowCreateSession(false);
            setSelectedTemplate(null);
          }}
          onCreated={() => {
            loadWorkoutData();
            setShowCreateSession(false);
            setSelectedTemplate(null);
          }}
        />
      )}
    </div>
  );
}

// ============================================
// WORKOUT SESSION CARD
// ============================================

function WorkoutSessionCard({ session, onViewLeaderboard }: { session: SharedSession; onViewLeaderboard: () => void }) {
  const workout = session.workout_templates;
  const isActive = session.is_active && (!session.ends_at || new Date(session.ends_at) > new Date());
  const isGarminCompatible = canUploadToGarmin(workout?.sport_type);
  
  return (
    <div className={`bg-[#f8fafc] border rounded-xl p-4 ${
      isActive ? 'border-[#e2e8f0]' : 'border-gray-800 opacity-60'
    }`}>
      <div className="flex items-start justify-between mb-2">
        <div className="min-w-0 flex-1">
          <h3 className="font-bold text-base truncate">{session.name}</h3>
          <p className="text-xs text-gray-500 truncate">{workout?.name}</p>
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0 ml-2">
          <WorkoutStatusBadge status={session.my_status} />
          {!isGarminCompatible && (
            <span className="text-[10px] text-gray-500">Share only</span>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 text-xs text-gray-500 mb-3">
        {workout?.estimated_duration_minutes && (
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {workout.estimated_duration_minutes} min
          </span>
        )}
        {workout?.sport_type && (
          <span className="flex items-center gap-1">
            <Dumbbell className="w-3 h-3" />
            {workout.sport_type}
          </span>
        )}
        {isGarminCompatible && (
          <span className="flex items-center gap-1 text-blue-400">
            âŒš Garmin
          </span>
        )}
      </div>

      {session.ends_at && isActive && (
        <p className="text-[10px] text-yellow-400 mb-2">
          Ends {formatDistanceToNow(new Date(session.ends_at), { addSuffix: true })}
        </p>
      )}

      <div className="flex gap-2">
        <button
          onClick={onViewLeaderboard}
          className="flex-1 px-3 py-2 bg-green-500 hover:bg-green-400 text-[#0f172a] font-bold rounded-lg transition flex items-center justify-center gap-1.5 text-sm"
        >
          <Trophy className="w-4 h-4" />
          Leaderboard
        </button>
        <button className="px-3 py-2 bg-[#f1f5f9] hover:bg-[#f1f5f9] rounded-lg transition">
          <Share2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function WorkoutStatusBadge({ status }: { status?: string }) {
  if (status === 'completed') {
    return <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-[10px] rounded-full">âœ“ Done</span>;
  }
  if (status === 'pushed') {
    return <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-[10px] rounded-full">âŒš Synced</span>;
  }
  return <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-[10px] rounded-full">Pending</span>;
}

// ============================================
// WORKOUT TEMPLATE CARD
// ============================================

function WorkoutTemplateCard({ template, onShare }: { template: WorkoutTemplate; onShare: () => void }) {
  const [copied, setCopied] = useState(false);
  const isGarminCompatible = canUploadToGarmin(template.sport_type);

  const copyCode = () => {
    navigator.clipboard.writeText(template.share_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-xl p-4 hover:border-green-500/50 transition">
      <div className="flex items-start justify-between mb-2">
        <div className="min-w-0 flex-1">
          <h3 className="font-bold text-base truncate">{template.name}</h3>
          <div className="flex items-center gap-2">
            <p className="text-xs text-gray-500 capitalize">{template.sport_type}</p>
            {isGarminCompatible && (
              <span className="text-[10px] text-blue-400">âŒš Garmin</span>
            )}
          </div>
        </div>
        {template.difficulty && (
          <span className={`px-2 py-0.5 text-[10px] rounded-full flex-shrink-0 ml-2 ${
            template.difficulty === 'easy' ? 'bg-green-500/20 text-green-400' :
            template.difficulty === 'moderate' ? 'bg-yellow-500/20 text-yellow-400' :
            template.difficulty === 'hard' ? 'bg-orange-500/20 text-orange-400' :
            'bg-red-500/20 text-red-400'
          }`}>
            {template.difficulty}
          </span>
        )}
      </div>

      {template.description && (
        <p className="text-xs text-gray-500 mb-2 line-clamp-2">{template.description}</p>
      )}

      <div className="flex gap-3 text-xs text-gray-500 mb-3">
        {template.estimated_duration_minutes && (
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {template.estimated_duration_minutes} min
          </span>
        )}
        {template.estimated_distance_meters && (
          <span className="flex items-center gap-1">
            <Route className="w-3 h-3" />
            {(template.estimated_distance_meters / 1000).toFixed(1)} km
          </span>
        )}
      </div>

      <div className="flex gap-2">
        <button
          onClick={onShare}
          className="flex-1 px-3 py-2 bg-green-500 hover:bg-green-400 text-[#0f172a] font-bold rounded-lg transition text-sm"
        >
          Share
        </button>
        <button
          onClick={copyCode}
          className="px-3 py-2 bg-[#f1f5f9] hover:bg-[#f1f5f9] rounded-lg transition"
          title="Copy share code"
        >
          {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

// ============================================
// JOIN WORKOUT SESSION MODAL
// ============================================

function JoinWorkoutSessionModal({ onClose, onJoined }: { onClose: () => void; onJoined: () => void }) {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<'idle' | 'joining' | 'pushing' | 'success' | 'error'>('idle');
  const [error, setError] = useState('');
  const [joinedSession, setJoinedSession] = useState<any>(null);
  const [garminResult, setGarminResult] = useState<any>(null);

  const handleJoin = async () => {
    if (!code.trim()) return;
    
    setLoading(true);
    setError('');
    setStatus('joining');

    try {
      const supabase = getSupabase();
      const { data: { session } } = await supabase.auth.getSession();
      
      // Step 1: Join the session
      console.log('ðŸš€ Joining session with code:', code.toUpperCase());
      const joinResponse = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/join-shared-workouts`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({ 
            session_code: code.toUpperCase(),
            push_to_garmin: false  // We'll handle this from frontend
          })
        }
      );

      const joinResult = await joinResponse.json();
      console.log('ðŸ“¦ Join result:', joinResult);

      if (!joinResponse.ok) {
        setError(joinResult.error || 'Failed to join session');
        setStatus('error');
        setLoading(false);
        return;
      }

      setJoinedSession(joinResult);

      // Step 2: Push to Garmin (if it's a compatible workout type)
      if (joinResult.session_id) {
        setStatus('pushing');
        console.log('ðŸš€ Pushing to Garmin for session:', joinResult.session_id);
        
        try {
          const pushResponse = await fetch(
            `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/push-shared-workout-to-garmin`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session?.access_token}`,
              },
              body: JSON.stringify({ session_id: joinResult.session_id })
            }
          );

          const pushResult = await pushResponse.json();
          console.log('ðŸ“¦ Push result:', pushResult);
          setGarminResult(pushResult);

          if (pushResult.success) {
            setStatus('success');
          } else {
            // Still show success for joining, but note Garmin failed
            setStatus('success');
            console.warn('Garmin push failed:', pushResult.error);
          }
        } catch (pushErr: any) {
          console.error('Garmin push error:', pushErr);
          setGarminResult({ error: pushErr.message });
          setStatus('success'); // Still joined successfully
        }
      } else {
        setStatus('success');
      }

    } catch (err: any) {
      console.error('Join error:', err);
      setError(err.message || 'Something went wrong');
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  const handleDone = () => {
    onJoined();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-end md:items-center justify-center z-50 p-0 md:p-4">
      <div className="bg-[#f8fafc] border-t md:border border-[#e2e8f0] rounded-t-2xl md:rounded-2xl p-5 w-full md:max-w-md max-h-[85vh] overflow-y-auto safe-area-pb">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-xl font-bold">
            {status === 'success' ? 'ðŸŽ‰ Joined!' : 'Join Workout Session'}
          </h2>
          <button onClick={status === 'success' ? handleDone : onClose} className="p-2 hover:bg-[#f8fafc] rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Success State */}
        {status === 'success' && joinedSession && (
          <div className="space-y-4">
            <div className="p-4 bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-xl">
              <p className="font-bold">{joinedSession.session_name}</p>
              <p className="text-sm text-gray-500">{joinedSession.workout_name}</p>
            </div>

            {garminResult?.success ? (
              <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-xl">
                <p className="text-sm text-blue-400 flex items-center gap-2">
                  <Check className="w-4 h-4" />
                  Workout sent to your Garmin!
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Check your Garmin Connect app
                </p>
              </div>
            ) : garminResult?.error ? (
              <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-xl">
                <p className="text-sm text-yellow-400">
                  âš ï¸ Couldn't sync to Garmin
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {garminResult.error.includes('not connected') 
                    ? 'Connect your Garmin in Settings'
                    : garminResult.error
                  }
                </p>
              </div>
            ) : null}

            <button
              onClick={handleDone}
              className="w-full px-4 py-3 bg-green-500 hover:bg-green-400 text-[#0f172a] font-bold rounded-lg transition"
            >
              Done
            </button>
          </div>
        )}

        {/* Join Form */}
        {status !== 'success' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Session Code</label>
              <input
                type="text"
                placeholder="Enter 6-digit code"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                maxLength={6}
                disabled={loading}
                className="w-full px-4 py-3 bg-[#f8fafc] border border-gray-700 rounded-lg focus:border-green-500 outline-none text-center text-2xl tracking-widest font-mono disabled:opacity-50"
              />
            </div>

            {error && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}

            {/* Status indicator */}
            {loading && (
              <div className="p-3 bg-[#f8fafc] rounded-lg">
                <p className="text-sm text-gray-500 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {status === 'joining' && 'Joining session...'}
                  {status === 'pushing' && 'Sending to Garmin...'}
                </p>
              </div>
            )}

            <button
              onClick={handleJoin}
              disabled={code.length < 6 || loading}
              className="w-full px-4 py-3 bg-green-500 hover:bg-green-400 disabled:opacity-50 text-[#0f172a] font-bold rounded-lg transition flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Play className="w-5 h-5" />}
              Join & Sync to Garmin
            </button>

            <p className="text-xs text-gray-500 text-center">
              The workout will be sent to your connected Garmin device
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// CREATE WORKOUT SESSION MODAL
// ============================================

function CreateWorkoutSessionModal({ 
  template, 
  onClose, 
  onCreated 
}: { 
  template: WorkoutTemplate; 
  onClose: () => void; 
  onCreated: () => void;
}) {
  const [name, setName] = useState(`${template.name} Challenge`);
  const [description, setDescription] = useState('');
  const [duration, setDuration] = useState<'none' | '24h' | '48h' | '7d'>('7d');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [createdSession, setCreatedSession] = useState<any>(null);
  const [copied, setCopied] = useState(false);

  const isGarminCompatible = canUploadToGarmin(template.sport_type);

  const handleCreate = async () => {
    setLoading(true);
    setError('');

    try {
      const user = await currentUser();
      if (!user) {
        setError('You must be logged in to create a session');
        setLoading(false);
        return;
      }

      const supabase = getSupabase();

      let endsAt = null;
      if (duration !== 'none') {
        const end = new Date();
        if (duration === '24h') end.setHours(end.getHours() + 24);
        else if (duration === '48h') end.setHours(end.getHours() + 48);
        else if (duration === '7d') end.setDate(end.getDate() + 7);
        endsAt = end.toISOString();
      }

      console.log('Creating session with:', {
        workout_template_id: template.id,
        creator_id: user.id,
        name,
        description,
        starts_at: new Date().toISOString(),
        ends_at: endsAt
      });

      const { data, error: insertError } = await supabase
        .from('shared_workout_sessions')
        .insert({
          workout_template_id: template.id,
          creator_id: user.id,
          name,
          description: description || null,
          starts_at: new Date().toISOString(),
          ends_at: endsAt
        })
        .select()
        .single();

      console.log('Insert result:', { data, error: insertError });

      if (insertError) {
        console.error('Error creating session:', insertError);
        setError(insertError.message || 'Failed to create session');
        setLoading(false);
        return;
      }

      if (!data) {
        setError('No data returned from insert');
        setLoading(false);
        return;
      }

      // Add creator as participant
      const { error: participantError } = await supabase
        .from('shared_workout_participants')
        .insert({
          session_id: data.id,
          user_id: user.id,
          garmin_push_status: 'pending'
        });

      if (participantError) {
        console.error('Error adding participant:', participantError);
        // Don't fail the whole thing, session was created
      }

      // Show the created session with share code
      setCreatedSession(data);
      
    } catch (err: any) {
      console.error('Unexpected error:', err);
      setError(err.message || 'Something went wrong');
    }

    setLoading(false);
  };

  const copyCode = () => {
    if (createdSession?.session_code) {
      navigator.clipboard.writeText(createdSession.session_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDone = () => {
    onCreated();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-end md:items-center justify-center z-50 p-0 md:p-4">
      <div className="bg-[#f8fafc] border-t md:border border-[#e2e8f0] rounded-t-2xl md:rounded-2xl p-5 w-full md:max-w-md max-h-[85vh] overflow-y-auto safe-area-pb">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-xl font-bold">
            {createdSession ? 'ðŸŽ‰ Session Created!' : 'Share Workout'}
          </h2>
          <button onClick={createdSession ? handleDone : onClose} className="p-2 hover:bg-[#f8fafc] rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Success State - Show Share Code */}
        {createdSession ? (
          <div className="space-y-4">
            <div className="p-4 bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-xl text-center">
              <p className="text-sm text-gray-500 mb-2">Share this code with friends:</p>
              <p className="text-3xl font-mono font-bold tracking-widest text-green-400">
                {createdSession.session_code || 'N/A'}
              </p>
            </div>

            <button
              onClick={copyCode}
              className="w-full px-4 py-3 bg-[#f8fafc] hover:bg-[#f1f5f9] border border-gray-700 rounded-lg transition flex items-center justify-center gap-2"
            >
              {copied ? <Check className="w-5 h-5 text-green-400" /> : <Copy className="w-5 h-5" />}
              {copied ? 'Copied!' : 'Copy Code'}
            </button>

            <div className="p-3 bg-[#f8fafc] rounded-xl">
              <p className="font-medium text-sm">{createdSession.name}</p>
              <p className="text-xs text-gray-500">
                {createdSession.ends_at 
                  ? `Ends ${formatDistanceToNow(new Date(createdSession.ends_at), { addSuffix: true })}`
                  : 'No end date'
                }
              </p>
            </div>

            <button
              onClick={handleDone}
              className="w-full px-4 py-3 bg-green-500 hover:bg-green-400 text-[#0f172a] font-bold rounded-lg transition"
            >
              Done
            </button>
          </div>
        ) : (
          /* Create Form */
          <>
            <div className="p-3 bg-[#f8fafc] rounded-xl mb-4">
              <p className="font-medium text-sm">{template.name}</p>
              <div className="flex items-center gap-2">
                <p className="text-xs text-gray-500">{template.sport_type} â€¢ {template.estimated_duration_minutes} min</p>
                {isGarminCompatible ? (
                  <span className="text-[10px] text-blue-400">âŒš Garmin</span>
                ) : (
                  <span className="text-[10px] text-gray-500">Share only</span>
                )}
              </div>
            </div>

            {!isGarminCompatible && (
              <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg mb-4">
                <p className="text-xs text-yellow-400">
                  âš ï¸ {template.sport_type} can't sync to Garmin. Only running, cycling, and swimming.
                </p>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Session Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-2 bg-[#f8fafc] border border-gray-700 rounded-lg focus:border-green-500 outline-none text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Duration</label>
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { id: 'none', label: 'Open' },
                    { id: '24h', label: '24h' },
                    { id: '48h', label: '48h' },
                    { id: '7d', label: '7d' }
                  ].map(opt => (
                    <button
                      key={opt.id}
                      onClick={() => setDuration(opt.id as any)}
                      className={`px-2 py-2 rounded-lg text-xs font-medium transition ${
                        duration === opt.id ? 'bg-green-500 text-[#0f172a]' : 'bg-[#f8fafc] text-gray-500'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <p className="text-sm text-red-400">{error}</p>
                </div>
              )}

              <button
                onClick={handleCreate}
                disabled={!name.trim() || loading}
                className="w-full px-4 py-3 bg-green-500 hover:bg-green-400 disabled:opacity-50 text-[#0f172a] font-bold rounded-lg transition flex items-center justify-center gap-2"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Share2 className="w-5 h-5" />}
                Create & Get Code
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ============================================
// CREATE WORKOUT MODAL - Mobile Optimized
// ============================================

function CreateWorkoutModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [step, setStep] = useState<'sport' | 'type' | 'configure' | 'review'>('sport');
  const [loading, setLoading] = useState(false);
  
  // Basic info
  const [sportType, setSportType] = useState<'running' | 'cycling' | 'swimming'>('running');
  const [workoutType, setWorkoutType] = useState<'intervals' | 'continuous'>('intervals');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [difficulty, setDifficulty] = useState<'easy' | 'moderate' | 'hard' | 'intense'>('moderate');
  
  // Warmup/Cooldown
  const [includeWarmup, setIncludeWarmup] = useState(true);
  const [warmupDuration, setWarmupDuration] = useState(10);
  const [includeCooldown, setIncludeCooldown] = useState(true);
  const [cooldownDuration, setCooldownDuration] = useState(5);
  
  // Main workout configuration
  const [measureBy, setMeasureBy] = useState<'distance' | 'time'>('distance');
  
  // Interval config
  const [rounds, setRounds] = useState(4);
  const [workValue, setWorkValue] = useState(400);
  const [workIntensity, setWorkIntensity] = useState<'steady' | 'tempo' | 'threshold' | '5km' | '10km'>('tempo');
  const [restDuration, setRestDuration] = useState(90);
  
  // Continuous config
  const [continuousValue, setContinuousValue] = useState(5000);
  const [continuousIntensity, setContinuousIntensity] = useState<'easy' | 'steady' | 'tempo'>('steady');

  const sportLabels = {
    running: { icon: '🏃', distanceUnit: 'm', defaultWork: 400, defaultContinuous: 5000 },
    cycling: { icon: '🚴', distanceUnit: 'm', defaultWork: 1000, defaultContinuous: 20000 },
    swimming: { icon: '🏊', distanceUnit: 'm', defaultWork: 100, defaultContinuous: 1500 }
  };

  const generateName = () => {
    if (workoutType === 'intervals') {
      const unit = measureBy === 'distance' ? 'm' : 's';
      return `${rounds}x${workValue}${unit} ${workIntensity.charAt(0).toUpperCase() + workIntensity.slice(1)} ${sportType.charAt(0).toUpperCase() + sportType.slice(1)}`;
    } else {
      const value = measureBy === 'distance' 
        ? `${(continuousValue / 1000).toFixed(1)}km` 
        : `${continuousValue}min`;
      return `${value} ${continuousIntensity.charAt(0).toUpperCase() + continuousIntensity.slice(1)} ${sportType.charAt(0).toUpperCase() + sportType.slice(1)}`;
    }
  };

  const calculateEstimates = () => {
    let totalDistance = 0;
    let totalTime = 0;
    
    if (includeWarmup) {
      totalTime += warmupDuration;
      totalDistance += (warmupDuration / 60) * 10000;
    }
    
    if (workoutType === 'intervals') {
      for (let i = 0; i < rounds; i++) {
        if (measureBy === 'distance') {
          totalDistance += workValue;
          const paceMap: Record<string, number> = { steady: 5.5, tempo: 5.0, threshold: 4.5, '5km': 4.0, '10km': 4.3 };
          totalTime += (workValue / 1000) * (paceMap[workIntensity] || 5.0);
        } else {
          totalTime += workValue / 60;
          const speedMap: Record<string, number> = { steady: 11, tempo: 12, threshold: 13.5, '5km': 15, '10km': 14 };
          totalDistance += (workValue / 3600) * (speedMap[workIntensity] || 12) * 1000;
        }
        if (i < rounds - 1) {
          totalTime += restDuration / 60;
        }
      }
    } else {
      if (measureBy === 'distance') {
        totalDistance += continuousValue;
        const paceMap: Record<string, number> = { easy: 6.0, steady: 5.5, tempo: 5.0 };
        totalTime += (continuousValue / 1000) * (paceMap[continuousIntensity] || 5.5);
      } else {
        totalTime += continuousValue;
        const speedMap: Record<string, number> = { easy: 10, steady: 11, tempo: 12 };
        totalDistance += (continuousValue / 60) * (speedMap[continuousIntensity] || 11) * 1000;
      }
    }
    
    if (includeCooldown) {
      totalTime += cooldownDuration;
      totalDistance += (cooldownDuration / 60) * 9000;
    }
    
    return { distance: Math.round(totalDistance), duration: Math.round(totalTime) };
  };

  const buildWorkoutSteps = (): WorkoutStep[] => {
    const steps: WorkoutStep[] = [];
    
    if (includeWarmup) {
      steps.push({
        type: 'warmup',
        duration: { type: 'time', value: warmupDuration },
        intensity: 'easy',
        description: `Easy ${sportType} warmup`
      });
    }
    
    if (workoutType === 'intervals') {
      for (let i = 0; i < rounds; i++) {
        steps.push({
          type: 'work',
          duration: { type: measureBy, value: measureBy === 'distance' ? workValue : workValue / 60 },
          intensity: workIntensity,
          description: `Interval ${i + 1} of ${rounds}`
        });
        if (i < rounds - 1) {
          steps.push({
            type: 'rest',
            duration: { type: 'time', value: restDuration / 60 },
            intensity: 'recovery',
            description: 'Recovery'
          });
        }
      }
    } else {
      steps.push({
        type: 'work',
        duration: { type: measureBy, value: continuousValue },
        intensity: continuousIntensity,
        description: `Main ${sportType} effort`
      });
    }
    
    if (includeCooldown) {
      steps.push({
        type: 'cooldown',
        duration: { type: 'time', value: cooldownDuration },
        intensity: 'easy',
        description: `Easy ${sportType} cooldown`
      });
    }
    
    return steps;
  };

  const handleCreate = async () => {
    setLoading(true);
    const user = await currentUser();
    const supabase = getSupabase();
    const workoutSteps = buildWorkoutSteps();
    const estimates = calculateEstimates();

    const { error } = await supabase
      .from('workout_templates')
      .insert({
        creator_id: user?.id,
        name: name || generateName(),
        description,
        sport_type: sportType,
        difficulty,
        estimated_duration_minutes: estimates.duration,
        estimated_distance_meters: estimates.distance,
        workout_structure: { segments: [{ name: 'Main Workout', steps: workoutSteps }] }
      });

    if (!error) onCreated();
    setLoading(false);
  };

  const estimates = calculateEstimates();

  return (
    <div className="fixed inset-0 bg-black/80 flex items-end md:items-center justify-center z-50 p-0 md:p-4">
      <div className="bg-[#f8fafc] border-t md:border border-[#e2e8f0] rounded-t-2xl md:rounded-2xl p-5 w-full md:max-w-lg max-h-[90vh] overflow-y-auto safe-area-pb">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">Create Workout</h2>
          <button onClick={onClose} className="p-2 hover:bg-[#f8fafc] rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress indicator */}
        <div className="flex items-center gap-1 mb-5">
          {['sport', 'type', 'configure', 'review'].map((s, i) => (
            <div key={s} className="flex items-center flex-1">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                step === s ? 'bg-green-500 text-[#0f172a]' : 
                ['sport', 'type', 'configure', 'review'].indexOf(step) > i ? 'bg-green-500/30 text-green-400' :
                'bg-[#f1f5f9] text-gray-500'
              }`}>{i + 1}</div>
              {i < 3 && <div className={`flex-1 h-0.5 mx-1 rounded ${
                ['sport', 'type', 'configure', 'review'].indexOf(step) > i ? 'bg-green-500/50' : 'bg-[#f1f5f9]'
              }`} />}
            </div>
          ))}
        </div>

        {/* Step 1: Select Sport */}
        {step === 'sport' && (
          <div className="space-y-4">
            <p className="text-gray-500 text-sm">What type of workout?</p>
            <div className="grid grid-cols-3 gap-2">
              {(['running', 'cycling', 'swimming'] as const).map(sport => (
                <button
                  key={sport}
                  onClick={() => setSportType(sport)}
                  className={`p-3 rounded-xl border-2 transition flex flex-col items-center gap-1 ${
                    sportType === sport ? 'border-green-500 bg-green-500/10' : 'border-[#e2e8f0] hover:border-green-500/50'
                  }`}
                >
                  <span className="text-2xl">{sportLabels[sport].icon}</span>
                  <span className="font-medium text-xs capitalize">{sport}</span>
                </button>
              ))}
            </div>
            <button onClick={() => setStep('type')} className="w-full px-4 py-3 bg-green-500 hover:bg-green-400 text-[#0f172a] font-bold rounded-lg transition mt-2">
              Continue
            </button>
          </div>
        )}

        {/* Step 2: Workout Type */}
        {step === 'type' && (
          <div className="space-y-4">
            <p className="text-gray-500 text-sm">Choose structure:</p>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setWorkoutType('intervals')}
                className={`p-3 rounded-xl border-2 transition text-left ${
                  workoutType === 'intervals' ? 'border-green-500 bg-green-500/10' : 'border-[#e2e8f0] hover:border-green-500/50'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Zap className="w-4 h-4 text-yellow-400" />
                  <span className="font-bold text-sm">Intervals</span>
                </div>
                <p className="text-[10px] text-gray-500">Work + rest periods</p>
              </button>
              <button
                onClick={() => setWorkoutType('continuous')}
                className={`p-3 rounded-xl border-2 transition text-left ${
                  workoutType === 'continuous' ? 'border-green-500 bg-green-500/10' : 'border-[#e2e8f0] hover:border-green-500/50'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Route className="w-4 h-4 text-blue-400" />
                  <span className="font-bold text-sm">Continuous</span>
                </div>
                <p className="text-[10px] text-gray-500">Steady effort</p>
              </button>
            </div>
            <div className="flex gap-2 mt-2">
              <button onClick={() => setStep('sport')} className="flex-1 px-4 py-2 bg-[#f8fafc] hover:bg-[#f8fafc] rounded-lg font-medium text-sm">Back</button>
              <button onClick={() => setStep('configure')} className="flex-1 px-4 py-3 bg-green-500 hover:bg-green-400 text-[#0f172a] font-bold rounded-lg transition">Continue</button>
            </div>
          </div>
        )}

        {/* Step 3: Configure */}
        {step === 'configure' && (
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium mb-2">Measure by</label>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => setMeasureBy('distance')} className={`px-3 py-2 rounded-lg text-sm font-medium transition ${measureBy === 'distance' ? 'bg-green-500 text-[#0f172a]' : 'bg-[#f8fafc] text-gray-500'}`}>📏 Distance</button>
                <button onClick={() => setMeasureBy('time')} className={`px-3 py-2 rounded-lg text-sm font-medium transition ${measureBy === 'time' ? 'bg-green-500 text-[#0f172a]' : 'bg-[#f8fafc] text-gray-500'}`}>⏱️ Time</button>
              </div>
            </div>

            {workoutType === 'intervals' && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium mb-1">Rounds</label>
                    <div className="flex items-center gap-1">
                      <button onClick={() => setRounds(Math.max(1, rounds - 1))} className="w-9 h-9 bg-[#f8fafc] hover:bg-[#f1f5f9] rounded-lg font-bold text-sm">-</button>
                      <input type="number" value={rounds} onChange={(e) => setRounds(Math.max(1, parseInt(e.target.value) || 1))} className="flex-1 px-2 py-2 bg-[#f8fafc] border border-gray-700 rounded-lg text-center font-bold text-sm" />
                      <button onClick={() => setRounds(rounds + 1)} className="w-9 h-9 bg-[#f8fafc] hover:bg-[#f1f5f9] rounded-lg font-bold text-sm">+</button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Work {measureBy === 'distance' ? '(m)' : '(sec)'}</label>
                    <input type="number" value={workValue} onChange={(e) => setWorkValue(parseInt(e.target.value) || 0)} className="w-full px-3 py-2 bg-[#f8fafc] border border-gray-700 rounded-lg focus:border-green-500 outline-none text-sm" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Rest (seconds)</label>
                  <div className="flex gap-1">
                    {[60, 90, 120, 180].map(sec => (
                      <button key={sec} onClick={() => setRestDuration(sec)} className={`flex-1 px-2 py-2 rounded-lg text-xs font-medium transition ${restDuration === sec ? 'bg-green-500 text-[#0f172a]' : 'bg-[#f8fafc] text-gray-500'}`}>
                        {sec >= 60 ? `${Math.floor(sec / 60)}:${(sec % 60).toString().padStart(2, '0')}` : `${sec}s`}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Intensity</label>
                  <div className="flex flex-wrap gap-1">
                    {(['steady', 'tempo', 'threshold', '5km', '10km'] as const).map(int => (
                      <button key={int} onClick={() => setWorkIntensity(int)} className={`px-2 py-1.5 rounded-lg text-xs font-medium capitalize transition ${workIntensity === int ? 'bg-green-500 text-[#0f172a]' : 'bg-[#f8fafc] text-gray-500'}`}>
                        {int === '5km' ? '5km' : int === '10km' ? '10km' : int}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            {workoutType === 'continuous' && (
              <>
                <div>
                  <label className="block text-xs font-medium mb-1">{measureBy === 'distance' ? 'Distance (m)' : 'Duration (min)'}</label>
                  {measureBy === 'distance' ? (
                    <div className="flex gap-1 mb-2">
                      {[3000, 5000, 10000].map(dist => (
                        <button key={dist} onClick={() => setContinuousValue(dist)} className={`flex-1 px-2 py-2 rounded-lg text-xs font-medium transition ${continuousValue === dist ? 'bg-green-500 text-[#0f172a]' : 'bg-[#f8fafc] text-gray-500'}`}>{dist / 1000}km</button>
                      ))}
                    </div>
                  ) : (
                    <div className="flex gap-1 mb-2">
                      {[20, 30, 45, 60].map(mins => (
                        <button key={mins} onClick={() => setContinuousValue(mins)} className={`flex-1 px-2 py-2 rounded-lg text-xs font-medium transition ${continuousValue === mins ? 'bg-green-500 text-[#0f172a]' : 'bg-[#f8fafc] text-gray-500'}`}>{mins}m</button>
                      ))}
                    </div>
                  )}
                  <input type="number" value={continuousValue} onChange={(e) => setContinuousValue(parseInt(e.target.value) || 0)} className="w-full px-3 py-2 bg-[#f8fafc] border border-gray-700 rounded-lg focus:border-green-500 outline-none text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Intensity</label>
                  <div className="grid grid-cols-3 gap-1">
                    {(['easy', 'steady', 'tempo'] as const).map(int => (
                      <button key={int} onClick={() => setContinuousIntensity(int)} className={`px-2 py-2 rounded-lg text-xs font-medium capitalize transition ${continuousIntensity === int ? 'bg-green-500 text-[#0f172a]' : 'bg-[#f8fafc] text-gray-500'}`}>{int}</button>
                    ))}
                  </div>
                </div>
              </>
            )}

            <div className="p-3 bg-[#f8fafc] rounded-xl space-y-2">
              <p className="text-xs font-medium text-gray-600">Warmup & Cooldown</p>
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={includeWarmup} onChange={(e) => setIncludeWarmup(e.target.checked)} className="w-4 h-4 accent-green-500" />
                  <span className="text-xs">Warmup</span>
                </label>
                {includeWarmup && (
                  <div className="flex items-center gap-1">
                    <input type="number" value={warmupDuration} onChange={(e) => setWarmupDuration(parseInt(e.target.value) || 5)} className="w-12 px-2 py-1 bg-[#ffffff] border border-gray-700 rounded text-center text-xs" />
                    <span className="text-[10px] text-gray-500">min</span>
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={includeCooldown} onChange={(e) => setIncludeCooldown(e.target.checked)} className="w-4 h-4 accent-green-500" />
                  <span className="text-xs">Cooldown</span>
                </label>
                {includeCooldown && (
                  <div className="flex items-center gap-1">
                    <input type="number" value={cooldownDuration} onChange={(e) => setCooldownDuration(parseInt(e.target.value) || 5)} className="w-12 px-2 py-1 bg-[#ffffff] border border-gray-700 rounded text-center text-xs" />
                    <span className="text-[10px] text-gray-500">min</span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-2">
              <button onClick={() => setStep('type')} className="flex-1 px-4 py-2 bg-[#f8fafc] hover:bg-[#f8fafc] rounded-lg font-medium text-sm">Back</button>
              <button onClick={() => setStep('review')} className="flex-1 px-4 py-3 bg-green-500 hover:bg-green-400 text-[#0f172a] font-bold rounded-lg transition">Review</button>
            </div>
          </div>
        )}

        {/* Step 4: Review */}
        {step === 'review' && (
          <div className="space-y-3">
            <div className="p-3 bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-xl">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">{sportLabels[sportType].icon}</span>
                <div>
                  <p className="font-bold text-sm">{name || generateName()}</p>
                  <p className="text-xs text-gray-500 capitalize">{sportType} â€¢ {workoutType}</p>
                </div>
              </div>
              <div className="flex gap-3 text-xs">
                <span className="flex items-center gap-1 text-gray-600"><Clock className="w-3 h-3" />~{estimates.duration} min</span>
                <span className="flex items-center gap-1 text-gray-600"><Route className="w-3 h-3" />~{(estimates.distance / 1000).toFixed(1)} km</span>
              </div>
            </div>

            <div className="p-3 bg-[#f8fafc] rounded-xl">
              <p className="text-xs font-medium mb-1 text-gray-600">Structure:</p>
              <div className="space-y-0.5 text-xs text-gray-500">
                {includeWarmup && <p>â€¢ {warmupDuration}min warmup</p>}
                {workoutType === 'intervals' ? (
                  <p>â€¢ {rounds}x {workValue}{measureBy === 'distance' ? 'm' : 's'} @ {workIntensity}</p>
                ) : (
                  <p>â€¢ {measureBy === 'distance' ? `${(continuousValue/1000).toFixed(1)}km` : `${continuousValue}min`} @ {continuousIntensity}</p>
                )}
                {includeCooldown && <p>â€¢ {cooldownDuration}min cooldown</p>}
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">Name</label>
              <input type="text" placeholder={generateName()} value={name} onChange={(e) => setName(e.target.value)} className="w-full px-3 py-2 bg-[#f8fafc] border border-gray-700 rounded-lg focus:border-green-500 outline-none text-sm" />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">Difficulty</label>
              <div className="grid grid-cols-4 gap-1">
                {(['easy', 'moderate', 'hard', 'intense'] as const).map(diff => (
                  <button key={diff} onClick={() => setDifficulty(diff)} className={`px-2 py-1.5 rounded-lg text-xs font-medium capitalize transition ${difficulty === diff ? 'bg-green-500 text-[#0f172a]' : 'bg-[#f8fafc] text-gray-500'}`}>{diff}</button>
                ))}
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <button onClick={() => setStep('configure')} className="flex-1 px-4 py-2 bg-[#f8fafc] hover:bg-[#f8fafc] rounded-lg font-medium text-sm">Back</button>
              <button onClick={handleCreate} disabled={loading} className="flex-1 px-4 py-3 bg-green-500 hover:bg-green-400 disabled:opacity-50 text-[#0f172a] font-bold rounded-lg transition flex items-center justify-center gap-2">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
                Create
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// MAIN LEADERBOARD COMPONENT
// ============================================

export default function Leaderboard() {
  const [view, setView] = useState<'leaderboards' | 'challenges' | 'friends' | 'workouts'>('leaderboards');
  const [tab, setTab] = useState('compliance');
  const [timeframe, setTimeframe] = useState('week');
  const [leaderboardType, setLeaderboardType] = useState<'global' | 'friends' | 'custom'>('global');
  
  // Data states
  const [leaderboard, setLeaderboard] = useState<any[]>([]);
  const [challenges, setChallenges] = useState<any[]>([]);
  const [friends, setFriends] = useState<any[]>([]);
  const [pendingRequests, setPendingRequests] = useState<any[]>([]);
  const [customLeaderboards, setCustomLeaderboards] = useState<any[]>([]);
  const [selectedSquad, setSelectedSquad] = useState<string | null>(null);
  
  // UI states
  const [userRank, setUserRank] = useState<number | null>(null);
  const [showInLeaderboard, setShowInLeaderboard] = useState(true);
  const [showCreateChallenge, setShowCreateChallenge] = useState(false);
  const [showCreateCustomLeaderboard, setShowCreateCustomLeaderboard] = useState(false);
  const [showInviteFriends, setShowInviteFriends] = useState(false);
  const [showCreateWorkout, setShowCreateWorkout] = useState(false);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, [view, tab, timeframe, leaderboardType, selectedSquad]);

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
  };

  // Process pending friend invites from JoinFriend page
  useEffect(() => {
    const processPendingInvite = async () => {
      const pendingInvite = localStorage.getItem('pendingFriendInvite');
      if (!pendingInvite) return;
      
      localStorage.removeItem('pendingFriendInvite');
      
      try {
        await callEdgeFunction('send-friend-request', { friend_id: pendingInvite });
        showToast('Friend request sent!', 'success');
        setView('friends');
      } catch (err: any) {
        if (err.message?.includes('already friends')) {
          showToast('You\'re already friends!', 'success');
          setView('friends');
        } else if (err.message?.includes('already pending')) {
          showToast('Friend request already pending', 'success');
          setView('friends');
        } else {
          showToast(err.message || 'Failed to send friend request', 'error');
        }
      }
    };

    processPendingInvite();
  }, []);

  const loadData = async () => {
    // Skip loading for workouts view - it handles its own data
    if (view === 'workouts') {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const user = await currentUser();
      if (!user) return;

      const supabase = getSupabase();
      
      // Load profile
      const { data: profile } = await supabase
        .from('clients')
        .select('show_in_leaderboard, partner_id, name')
        .eq('user_id', user.id)
        .single();

      setShowInLeaderboard(profile?.show_in_leaderboard ?? true);

      // Always load friends for tab count display
await loadFriendsFixed(user.id);

if (view === 'leaderboards') {
  await loadLeaderboard(user.id, profile?.partner_id);
} else if (view === 'challenges') {
  await loadChallenges(user.id);
}
// Note: friends already loaded above
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadLeaderboard = async (userId: string, partnerId?: string) => {
    const supabase = getSupabase();
    
    let data: any[] = [];
    
    if (leaderboardType === 'global') {
      const { data: result, error } = await supabase.rpc('get_leaderboard', {
        p_tab: tab,
        p_timeframe: timeframe,
        p_partner_id: partnerId || null
      });
      if (!error) data = result || [];
    } else if (leaderboardType === 'friends') {
      const { data: result, error } = await supabase.rpc('get_friends_leaderboard', {
        p_user_id: userId,
        p_tab: tab,
        p_timeframe: timeframe
      });
      if (!error) data = result || [];
    } else if (leaderboardType === 'custom' && selectedSquad) {
      const { data: result, error } = await supabase.rpc('get_custom_leaderboard', {
        p_leaderboard_id: selectedSquad,
        p_tab: tab,
        p_timeframe: timeframe
      });
      if (!error) data = result || [];
    }
    
    // Also load custom leaderboards list
    if (leaderboardType === 'custom') {
      const { data: squads } = await supabase
        .from('custom_leaderboards')
        .select('*')
        .contains('members', [userId]);
      setCustomLeaderboards(squads || []);
    }
    
    setLeaderboard(data);
    const rank = data.findIndex(d => d.user_id === userId) + 1;
    setUserRank(rank > 0 ? rank : null);
  };

  const loadChallenges = async (userId: string) => {
    const supabase = getSupabase();
    
    console.log('Loading challenges for user:', userId);
    
    const { data: challengesData, error: challengesError } = await supabase
      .from('challenges')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (challengesError) {
      console.error('Error loading challenges:', challengesError);
      setChallenges([]);
      return;
    }
    
    console.log('Raw challenges:', challengesData);
    
    if (!challengesData || challengesData.length === 0) {
      setChallenges([]);
      return;
    }
    
    const challengeIds = challengesData.map(c => c.id);
    const { data: participantsData, error: participantsError } = await supabase
      .from('challenge_participants')
      .select('*')
      .in('challenge_id', challengeIds);
    
    if (participantsError) {
      console.error('Error loading participants:', participantsError);
    }
    
    console.log('Raw participants:', participantsData);
    
    const participantUserIds = [...new Set((participantsData || []).map(p => p.user_id))];
    let clientsMap: Record<string, any> = {};
    
    if (participantUserIds.length > 0) {
      const { data: clientsData } = await supabase
        .from('clients')
        .select('user_id, name, avatar')
        .in('user_id', participantUserIds);
      
      clientsMap = (clientsData || []).reduce((acc, client) => {
        acc[client.user_id] = client;
        return acc;
      }, {} as Record<string, any>);
    }
    
    const challengesWithParticipants = challengesData.map(challenge => ({
      ...challenge,
      challenge_participants: (participantsData || [])
        .filter(p => p.challenge_id === challenge.id)
        .map(p => ({
          ...p,
          clients: clientsMap[p.user_id] || { name: 'Unknown', avatar: null }
        }))
    }));
    
    const userChallenges = challengesWithParticipants.filter(challenge => {
      const isCreator = challenge.creator_id === userId;
      const isParticipant = challenge.challenge_participants?.some(
        (p: any) => p.user_id === userId
      );
      return isCreator || isParticipant;
    });
    
    console.log('Final user challenges:', userChallenges);
    setChallenges(userChallenges);
  };

 // ============================================
// FIXED loadFriends FUNCTION
// Replace the loadFriends function in Leaderboard.tsx
// ============================================

const loadFriends = async (userId: string) => {
  const supabase = getSupabase();
  
  console.log('Loading friends for user:', userId);
  
  // First, get all accepted friendships where I'm involved
  // Query 1: Where I sent the request (I'm user_id, friend is friend_id)
  const { data: iSentRequests, error: err1 } = await supabase
    .from('friendships')
    .select('id, user_id, friend_id, status, created_at')
    .eq('user_id', userId)
    .eq('status', 'accepted');

  console.log('Friendships where I sent request:', iSentRequests, err1);

  // Query 2: Where I received the request (I'm friend_id, friend is user_id)
  const { data: iReceivedRequests, error: err2 } = await supabase
    .from('friendships')
    .select('id, user_id, friend_id, status, created_at')
    .eq('friend_id', userId)
    .eq('status', 'accepted');

  console.log('Friendships where I received request:', iReceivedRequests, err2);

  // Collect all friend user IDs
  const friendIds: string[] = [];
  
  // From requests I sent, the friend is friend_id
  (iSentRequests || []).forEach(f => {
    if (f.friend_id) friendIds.push(f.friend_id);
  });
  
  // From requests I received, the friend is user_id
  (iReceivedRequests || []).forEach(f => {
    if (f.user_id) friendIds.push(f.user_id);
  });

  console.log('Friend IDs to lookup:', friendIds);

  if (friendIds.length === 0) {
    setFriends([]);
    return;
  }

  // Now fetch the client info for all friends
  const { data: friendsData, error: err3 } = await supabase
    .from('clients')
    .select('user_id, name, avatar')
    .in('user_id', friendIds);

  console.log('Friends client data:', friendsData, err3);

  // Map the data back to the expected format
  const friendsWithData = friendIds.map(friendId => {
    const clientInfo = friendsData?.find(c => c.user_id === friendId);
    const friendship = [...(iSentRequests || []), ...(iReceivedRequests || [])]
      .find(f => f.friend_id === friendId || f.user_id === friendId);
    
    return {
      id: friendship?.id,
      friend: clientInfo || { user_id: friendId, name: 'Unknown', avatar: null }
    };
  });

  console.log('Final friends list:', friendsWithData);
  setFriends(friendsWithData);
};


// ============================================
// ALSO FIX: loadPendingRequests part
// ============================================

// In the same loadFriends function, also update how pending requests are loaded:
const loadPendingRequests = async (userId: string) => {
  const supabase = getSupabase();
  
  // Get pending requests where I'm the recipient
  const { data: pendingFriendships, error: err1 } = await supabase
    .from('friendships')
    .select('id, user_id, friend_id, status, created_at')
    .eq('friend_id', userId)
    .eq('status', 'pending');

  console.log('Pending friendships:', pendingFriendships, err1);

  if (!pendingFriendships || pendingFriendships.length === 0) {
    setPendingRequests([]);
    return;
  }

  // Get the requester's info
  const requesterIds = pendingFriendships.map(f => f.user_id);
  
  const { data: requestersData, error: err2 } = await supabase
    .from('clients')
    .select('user_id, name, avatar')
    .in('user_id', requesterIds);

  console.log('Requesters data:', requestersData, err2);

  const pendingWithData = pendingFriendships.map(friendship => {
    const requester = requestersData?.find(c => c.user_id === friendship.user_id);
    return {
      id: friendship.id,
      requester: requester || { user_id: friendship.user_id, name: 'Unknown', avatar: null }
    };
  });

  setPendingRequests(pendingWithData);
};


// ============================================
// COMPLETE REPLACEMENT for loadFriends in Leaderboard.tsx
// Copy this entire function
// ============================================

/*
Replace the existing loadFriends function with this:
*/

const loadFriendsFixed = async (userId: string) => {
  const supabase = getSupabase();
  
  try {
    // Get friendships where I'm the sender
    const { data: sentFriendships } = await supabase
      .from('friendships')
      .select('id, user_id, friend_id, status, created_at')
      .eq('user_id', userId)
      .eq('status', 'accepted');

    // Get friendships where I'm the recipient
    const { data: receivedFriendships } = await supabase
      .from('friendships')
      .select('id, user_id, friend_id, status, created_at')
      .eq('friend_id', userId)
      .eq('status', 'accepted');

    // Collect friend IDs
    const friendIds: string[] = [];
    const friendshipMap: Record<string, any> = {};

    (sentFriendships || []).forEach(f => {
      friendIds.push(f.friend_id);
      friendshipMap[f.friend_id] = f;
    });

    (receivedFriendships || []).forEach(f => {
      friendIds.push(f.user_id);
      friendshipMap[f.user_id] = f;
    });

    if (friendIds.length === 0) {
      setFriends([]);
    } else {
      // Fetch client info
      const { data: clientsData } = await supabase
        .from('clients')
        .select('user_id, name, avatar')
        .in('user_id', friendIds);

      const friends = friendIds.map(friendId => ({
        id: friendshipMap[friendId]?.id,
        friend: clientsData?.find(c => c.user_id === friendId) || {
          user_id: friendId,
          name: 'Unknown',
          avatar: null
        }
      }));

      setFriends(friends);
    }

    // Load pending requests (where I'm the recipient)
    const { data: pendingFriendships } = await supabase
      .from('friendships')
      .select('id, user_id, friend_id, status, created_at')
      .eq('friend_id', userId)
      .eq('status', 'pending');

    if (!pendingFriendships || pendingFriendships.length === 0) {
      setPendingRequests([]);
    } else {
      const requesterIds = pendingFriendships.map(f => f.user_id);
      
      const { data: requestersData } = await supabase
        .from('clients')
        .select('user_id, name, avatar')
        .in('user_id', requesterIds);

      const pending = pendingFriendships.map(f => ({
        id: f.id,
        requester: requestersData?.find(c => c.user_id === f.user_id) || {
          user_id: f.user_id,
          name: 'Unknown',
          avatar: null
        }
      }));

      setPendingRequests(pending);
    }

  } catch (error) {
    console.error('Error loading friends:', error);
    setFriends([]);
    setPendingRequests([]);
  }
};
  // â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”
  // EDGE FUNCTION HANDLERS
  // â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”â€”

  const createChallenge = async (challengeData: any) => {
    try {
      const result = await callEdgeFunction('create-challenge', {
        name: challengeData.name,
        type: challengeData.type,
        start_date: challengeData.start_date,
        end_date: challengeData.end_date,
        metric: challengeData.metric,
        invited_friends: challengeData.invited_friends || []
      });
      
      showToast('Challenge created successfully!', 'success');
      setShowCreateChallenge(false);
      
      const user = await currentUser();
      if (user) loadChallenges(user.id);
      
      return result.challenge_id;
    } catch (error: any) {
      showToast(error.message || 'Failed to create challenge', 'error');
      throw error;
    }
  };

  const joinChallenge = async (challengeId: string) => {
    try {
      await callEdgeFunction('join-challenge', { challenge_id: challengeId });
      showToast('Successfully joined the challenge!', 'success');
      
      const user = await currentUser();
      if (user) loadChallenges(user.id);
    } catch (error: any) {
      showToast(error.message || 'Failed to join challenge', 'error');
    }
  };

  const sendFriendRequest = async (friendId: string) => {
    try {
      await callEdgeFunction('send-friend-request', { friend_id: friendId });
      showToast('Friend request sent!', 'success');
    } catch (error: any) {
      showToast(error.message || 'Failed to send friend request', 'error');
    }
  };

  const acceptFriendRequest = async (friendshipId: string) => {
    try {
      await callEdgeFunction('accept-friend-request', { friendship_id: friendshipId });
      showToast('Friend request accepted!', 'success');
      
      const user = await currentUser();
      if (user) loadFriends(user.id);
    } catch (error: any) {
      showToast(error.message || 'Failed to accept friend request', 'error');
    }
  };

  const shareChallenge = (challenge: any) => {
    const link = `${window.location.origin}/join-challenge/${challenge.id}`;
    navigator.clipboard.writeText(link);
    showToast('Challenge link copied!', 'success');
  };

  const createCustomLeaderboard = async (name: string, description: string, memberIds: string[]) => {
    try {
      const supabase = getSupabase();
      const user = await currentUser();
      if (!user) throw new Error('Not authenticated');
      
      const { error } = await supabase
        .from('custom_leaderboards')
        .insert({
          creator_id: user.id,
          name,
          description,
          members: [user.id, ...memberIds]
        });
      
      if (error) throw error;
      
      showToast('Squad created successfully!', 'success');
      setShowCreateCustomLeaderboard(false);
      loadData();
    } catch (error: any) {
      showToast(error.message || 'Failed to create squad', 'error');
    }
  };

  return (
    <div className="flex h-screen bg-[#ffffff] text-[#0f172a]">
      <Sidebar />
      
      <main className="flex-1 overflow-y-auto">
        {/* Header */}
        <header className="sticky top-0 z-10 bg-[#ffffff] border-b border-[#e2e8f0] px-4 py-4 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 md:gap-4">
              {/* Mobile: Dashboard button */}
              <Link to="/dashboard" className="md:hidden p-2 bg-[#f1f5f9] hover:bg-[#f1f5f9] rounded-lg">
                <Home className="w-5 h-5 text-gray-500" />
              </Link>
              {/* Desktop: Back button */}
              <button onClick={() => navigate(-1)} className="hidden md:block">
                <ChevronLeft className="w-6 h-6" />
              </button>
              <h1 className="text-xl md:text-3xl font-bold flex items-center gap-2">
                <Trophy className="w-6 h-6 md:w-8 md:h-8 text-yellow-400" /> 
                <span className="hidden sm:inline">Social Hub</span>
                <span className="sm:hidden">Social</span>
              </h1>
            </div>

            {/* Quick Actions - Desktop */}
            <div className="hidden md:flex gap-2">
              <button
                onClick={() => {
                  setView('workouts');
                  setShowCreateWorkout(true);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-purple-500 hover:bg-purple-400 text-[#0f172a] font-bold rounded-xl transition"
              >
                <Dumbbell className="w-5 h-5" />
                Create Workout
              </button>
              <button
                onClick={() => setShowCreateChallenge(true)}
                className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-400 text-[#0f172a] font-bold rounded-xl transition"
              >
                <Target className="w-5 h-5" />
                Create Challenge
              </button>
              <button
                onClick={() => setShowInviteFriends(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-400 text-[#0f172a] font-bold rounded-xl transition"
              >
                <UserPlus className="w-5 h-5" />
                Invite Friends
              </button>
            </div>

            {/* Quick Actions - Mobile */}
            <div className="flex md:hidden gap-1.5">
              <button
                onClick={() => {
                  setView('workouts');
                  setShowCreateWorkout(true);
                }}
                className="p-2 bg-purple-500 hover:bg-purple-400 text-[#0f172a] rounded-lg"
                title="Create Workout"
              >
                <Dumbbell className="w-5 h-5" />
              </button>
              <button
                onClick={() => setShowCreateChallenge(true)}
                className="p-2 bg-green-500 hover:bg-green-400 text-[#0f172a] rounded-lg"
                title="Create Challenge"
              >
                <Target className="w-5 h-5" />
              </button>
              <button
                onClick={() => setShowInviteFriends(true)}
                className="p-2 bg-blue-500 hover:bg-blue-400 text-[#0f172a] rounded-lg"
                title="Invite Friends"
              >
                <UserPlus className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* View Tabs - Scrollable on mobile */}
          <div className="flex gap-1 overflow-x-auto pb-1 -mx-4 px-4 md:mx-0 md:px-0 md:border-b md:border-[#e2e8f0]">
            {[
  { id: 'leaderboards' as const, icon: Trophy, label: 'Leaderboard', shortLabel: 'Board' },
  { id: 'challenges' as const, icon: Target, label: 'Challenges', shortLabel: 'Challenges' },
  { 
    id: 'friends' as const, 
    icon: Users, 
    label: `Friends${friends.length > 0 ? ` (${friends.length})` : ''}`,  // Show count in label
    shortLabel: 'Friends', 
    badge: pendingRequests.length  // Badge shows pending requests
  },
  { id: 'workouts' as const, icon: Dumbbell, label: 'Workouts', shortLabel: 'Workouts' }
].map(({ id, icon: Icon, label, shortLabel, badge }) => (
              <button
                key={id}
                onClick={() => setView(id)}
                className={`flex items-center gap-1.5 px-3 py-2 md:px-6 md:py-3 font-medium border-b-2 transition relative whitespace-nowrap text-sm md:text-base ${
                  view === id
                    ? 'border-green-500 text-green-400'
                    : 'border-transparent text-gray-500 hover:text-[#0f172a]'
                }`}
              >
                <Icon className="w-4 h-4 md:w-5 md:h-5" />
                <span className="md:hidden">{shortLabel}</span>
                <span className="hidden md:inline">{label}</span>
                {badge > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 md:w-5 md:h-5 bg-red-500 rounded-full text-[10px] md:text-xs flex items-center justify-center text-[#0f172a]">
                    {badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </header>

        <div className="p-4 md:p-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-green-400" />
            </div>
          ) : (
            <>
              {/* LEADERBOARDS VIEW */}
              {view === 'leaderboards' && (
                <div className="space-y-4 md:space-y-6">
                  {/* Leaderboard Type Selector */}
                  <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0">
                    {[
                      { id: 'global' as const, label: '🌐 Global', icon: Trophy },
                      { id: 'friends' as const, label: '👥 Friends', icon: Users },
                      { id: 'custom' as const, label: '⚡ Squads', icon: Zap }
                    ].map(({ id, label, icon: Icon }) => (
                      <button
                        key={id}
                        onClick={() => {
                          setLeaderboardType(id);
                          setSelectedSquad(null);
                        }}
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-lg font-medium transition whitespace-nowrap text-sm ${
                          leaderboardType === id
                            ? 'bg-green-500 text-[#0f172a]'
                            : 'bg-[#f1f5f9] text-gray-500 hover:bg-[#f1f5f9]'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>

                  {/* Metric Tabs */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex gap-1 overflow-x-auto pb-1 -mx-4 px-4 md:mx-0 md:px-0">
                      {['compliance', 'progress', 'calorie'].map(t => (
                        <button
                          key={t}
                          onClick={() => setTab(t)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition whitespace-nowrap ${
                            tab === t ? 'bg-green-500 text-[#0f172a]' : 'bg-[#f1f5f9] text-gray-500 hover:bg-[#f1f5f9]'
                          }`}
                        >
                          {t === 'compliance' ? '📊 Logging' : 
                           t === 'progress' ? '🎯 Progress' : 
                           '🔥 Calories'}
                        </button>
                      ))}
                    </div>

                    <div className="flex gap-1">
                      {['week', 'month', 'all'].map(tf => (
                        <button
                          key={tf}
                          onClick={() => setTimeframe(tf)}
                          className={`px-2 py-1 rounded text-xs transition ${
                            timeframe === tf ? 'bg-[#f1f5f9] text-[#0f172a]' : 'text-gray-500 hover:text-[#0f172a]'
                          }`}
                        >
                          {tf === 'week' ? 'Week' : tf === 'month' ? 'Month' : 'All'}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Custom Leaderboards (Squads) */}
                  {leaderboardType === 'custom' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                      {customLeaderboards.map(squad => (
                        <div 
                          key={squad.id} 
                          onClick={() => setSelectedSquad(squad.id)}
                          className={`bg-[#f8fafc] border rounded-xl p-3 transition cursor-pointer ${
                            selectedSquad === squad.id 
                              ? 'border-green-500' 
                              : 'border-[#e2e8f0] hover:border-green-500/50'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <h3 className="font-bold text-sm">{squad.name}</h3>
                            <span className="text-[10px] text-gray-500">{squad.members?.length || 0} members</span>
                          </div>
                          <p className="text-xs text-gray-500 mb-2 truncate">{squad.description || 'No description'}</p>
                          <span className="text-[10px] text-green-400">
                            {selectedSquad === squad.id ? 'Selected' : 'Tap to view'}
                          </span>
                        </div>
                      ))}
                      
                      {/* Create Squad Card */}
                      <button
                        onClick={() => setShowCreateCustomLeaderboard(true)}
                        className="bg-[#f8fafc] border-2 border-dashed border-[#e2e8f0] rounded-xl p-4 hover:border-green-500/50 transition flex flex-col items-center justify-center gap-1 min-h-[100px]"
                      >
                        <Plus className="w-6 h-6 text-gray-500" />
                        <span className="font-medium text-gray-500 text-xs">Create Squad</span>
                      </button>
                    </div>
                  )}

                  {/* Main Leaderboard */}
                  <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-xl p-4 md:p-6">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-4">
                      <p className="text-xs text-gray-500">
                        Updated: {format(new Date(), 'HH:mm')}
                      </p>
                      <label className="flex items-center gap-2 text-xs">
                        <input
                          type="checkbox"
                          checked={showInLeaderboard}
                          onChange={async () => {
                            const supabase = getSupabase();
                            const user = await currentUser();
                            const newVal = !showInLeaderboard;
                            await supabase.from('clients').update({ show_in_leaderboard: newVal }).eq('user_id', user?.id);
                            setShowInLeaderboard(newVal);
                            loadData();
                          }}
                          className="w-4 h-4 accent-green-500"
                        />
                        Show me
                      </label>
                    </div>

                    {userRank && (
                      <div className="mb-4 p-3 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-lg text-center">
                        <p className="text-lg md:text-2xl font-bold flex items-center justify-center gap-2">
                          <Crown className="w-5 h-5 text-yellow-400" />
                          Your Rank: #{userRank}
                        </p>
                      </div>
                    )}

                    {leaderboard.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <Trophy className="w-12 h-12 mx-auto mb-3 opacity-20" />
                        <p className="text-sm">No data yet</p>
                        <p className="text-xs mt-1">Start logging to appear!</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {leaderboard.map((entry, i) => (
                          <div key={entry.user_id} className="flex items-center gap-2 md:gap-3 p-2 md:p-3 bg-[#f8fafc] rounded-lg hover:bg-[#f8fafc] transition group">
                            <div className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center font-bold text-sm md:text-lg flex-shrink-0 ${
                              i === 0 ? 'bg-yellow-500 text-[#0f172a]' :
                              i === 1 ? 'bg-gray-400 text-[#0f172a]' :
                              i === 2 ? 'bg-orange-600 text-[#0f172a]' :
                              'bg-gradient-to-br from-green-400 to-emerald-600 text-[#0f172a]'
                            }`}>
                              {i + 1}
                            </div>
                            <Avatar src={entry.avatar} name={entry.name} size="lg" className="border-2 border-green-400" />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm md:text-base truncate">{entry.name}</p>
                              <p className="text-[10px] md:text-xs text-gray-500">
                                {tab === 'compliance' ? `${entry.value}% logged` :
                                 tab === 'progress' ? `${entry.value}% to goal` :
                                 `${entry.value}% target hit`}
                              </p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className="text-xl md:text-2xl font-bold text-green-400">{entry.value}%</p>
                              {entry.streak > 0 && (
                                <div className="flex items-center justify-end gap-1 text-[10px] text-orange-400">
                                  <Flame className="w-3 h-3" />
                                  {entry.streak}d
                                </div>
                              )}
                            </div>
                            
                            {/* Quick Actions on Hover - Desktop only */}
                            <div className="hidden md:flex opacity-0 group-hover:opacity-100 transition gap-1">
                              <button 
                                onClick={() => sendFriendRequest(entry.user_id)}
                                className="p-1.5 bg-green-500/20 hover:bg-green-500/30 rounded-lg"
                              >
                                <UserPlus className="w-4 h-4 text-green-400" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* CHALLENGES VIEW */}
              {view === 'challenges' && (
                <div className="space-y-4 md:space-y-6">
                  {/* Active Challenges */}
                  <div>
                    <h2 className="text-lg md:text-xl font-bold mb-3 flex items-center gap-2">
                      <Zap className="w-5 h-5 text-yellow-400" />
                      Active Challenges
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {challenges.filter(c => new Date(c.end_date) > new Date()).map(challenge => (
                        <ChallengeCard 
                          key={challenge.id} 
                          challenge={challenge} 
                          onShare={() => shareChallenge(challenge)} 
                        />
                      ))}
                      {challenges.filter(c => new Date(c.end_date) > new Date()).length === 0 && (
                        <div className="col-span-full text-center py-8 text-gray-500">
                          <Target className="w-12 h-12 mx-auto mb-3 opacity-20" />
                          <p className="text-sm">No active challenges</p>
                          <p className="text-xs mt-1">Create one to get started!</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Past Challenges */}
                  {challenges.filter(c => new Date(c.end_date) <= new Date()).length > 0 && (
                    <div>
                      <h2 className="text-lg md:text-xl font-bold mb-3 flex items-center gap-2">
                        <Medal className="w-5 h-5 text-gray-500" />
                        Past Challenges
                      </h2>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {challenges.filter(c => new Date(c.end_date) <= new Date()).slice(0, 4).map(challenge => (
                          <ChallengeCard key={challenge.id} challenge={challenge} isPast />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* FRIENDS VIEW */}
              {view === 'friends' && (
                <div className="space-y-4 md:space-y-6">
                  {/* Pending Friend Requests */}
                  {pendingRequests.length > 0 && (
                    <div>
                      <h2 className="text-lg md:text-xl font-bold mb-3 flex items-center gap-2">
                        <UserPlus className="w-5 h-5 text-yellow-400" />
                        Pending ({pendingRequests.length})
                      </h2>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {pendingRequests.map(request => (
                          <div key={request.id} className="bg-[#f8fafc] border border-yellow-500/50 rounded-xl p-3">
                            <div className="flex items-center gap-2 mb-2">
                              <Avatar src={request.requester?.avatar} name={request.requester?.name} size="md" />
                              <div className="flex-1 min-w-0">
                                <h3 className="font-bold text-sm truncate">{request.requester?.name || 'Unknown'}</h3>
                                <p className="text-[10px] text-gray-500">Wants to be friends</p>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => acceptFriendRequest(request.id)}
                                className="flex-1 px-2 py-1.5 bg-green-500 hover:bg-green-400 text-[#0f172a] text-xs font-bold rounded-lg transition flex items-center justify-center gap-1"
                              >
                                <Check className="w-3 h-3" />
                                Accept
                              </button>
                              <button className="px-2 py-1.5 bg-[#f8fafc] hover:bg-[#f8fafc] rounded-lg transition">
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Friends List */}
                  <div>
                    <h2 className="text-lg md:text-xl font-bold mb-3 flex items-center gap-2">
                      <Users className="w-5 h-5 text-green-400" />
                      Friends ({friends.length})
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {friends.map(friendship => (
                        <FriendCard 
                          key={friendship.id} 
                          friend={friendship.friend}
                          onChallenge={() => setShowCreateChallenge(true)}
                        />
                      ))}
                      {friends.length === 0 && (
                        <div className="col-span-full text-center py-8 text-gray-500">
                          <Users className="w-12 h-12 mx-auto mb-3 opacity-20" />
                          <p className="text-sm">No friends yet</p>
                          <button
                            onClick={() => setShowInviteFriends(true)}
                            className="mt-3 px-4 py-2 bg-green-500 hover:bg-green-400 text-[#0f172a] font-bold rounded-lg transition text-sm"
                          >
                            Invite Friends
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* WORKOUTS VIEW */}
              {view === 'workouts' && (
                <SharedWorkouts />
              )}
            </>
          )}
        </div>

        {/* MODALS */}
        {showCreateChallenge && (
          <CreateChallengeModal
            onClose={() => setShowCreateChallenge(false)}
            onCreate={createChallenge}
            friends={friends}
          />
        )}

        {showCreateCustomLeaderboard && (
          <CreateCustomLeaderboardModal
            onClose={() => setShowCreateCustomLeaderboard(false)}
            onCreate={createCustomLeaderboard}
            friends={friends}
          />
        )}

        {showInviteFriends && (
          <InviteFriendsModal
            onClose={() => setShowInviteFriends(false)}
          />
        )}

        {showCreateWorkout && (
          <CreateWorkoutModal
            onClose={() => setShowCreateWorkout(false)}
            onCreated={() => {
              setShowCreateWorkout(false);
              // Reload workouts data if we're on the workouts view
              if (view === 'workouts') {
                loadData();
              }
            }}
          />
        )}
      </main>

      {/* Toast Notification */}
      {toast && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={() => setToast(null)} 
        />
      )}
    </div>
  );
}

// Challenge Card Component
function ChallengeCard({ challenge, isPast, onShare }: { challenge: any; isPast?: boolean; onShare?: () => void }) {
  const daysLeft = differenceInDays(new Date(challenge.end_date), new Date());
  const participants = challenge.challenge_participants || [];
  const topThree = [...participants].sort((a: any, b: any) => (b.score || 0) - (a.score || 0)).slice(0, 3);

  return (
    <div className={`bg-[#f8fafc] border rounded-xl p-4 ${
      isPast ? 'border-gray-800 opacity-60' : 'border-[#e2e8f0] hover:border-green-500/50'
    } transition`}>
      <div className="flex items-start justify-between mb-3">
        <div className="min-w-0 flex-1">
          <h3 className="font-bold text-sm md:text-lg mb-0.5 truncate">{challenge.name}</h3>
          <p className="text-[10px] md:text-xs text-gray-500">
            {isPast ? 'Ended' : `${Math.max(0, daysLeft)} days left`} â€¢ {participants.length} participants
          </p>
        </div>
        {!isPast && onShare && (
          <button
            onClick={onShare}
            className="p-1.5 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg transition flex-shrink-0 ml-2"
          >
            <Share2 className="w-4 h-4 text-blue-400" />
          </button>
        )}
      </div>

      {/* Challenge Progress */}
      <div className="space-y-1.5 mb-3">
        {topThree.map((participant: any, i: number) => (
          <div key={i} className="flex items-center gap-2">
            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
              i === 0 ? 'bg-yellow-500 text-[#0f172a]' :
              i === 1 ? 'bg-gray-400 text-[#0f172a]' :
              'bg-orange-600 text-[#0f172a]'
            }`}>
              {i + 1}
            </span>
            <Avatar src={participant.clients?.avatar} name={participant.clients?.name} size="sm" />
            <span className="flex-1 text-xs truncate">{participant.clients?.name || 'Unknown'}</span>
            <span className="font-bold text-green-400 text-xs">{Math.round(participant.score || 0)}%</span>
          </div>
        ))}
        {topThree.length === 0 && (
          <p className="text-xs text-gray-500 text-center py-2">No participants yet</p>
        )}
      </div>

      {isPast && topThree.length > 0 && (
        <div className="pt-2 border-t border-gray-800">
          <p className="text-[10px] text-gray-500">ðŸ† Winner: {topThree[0]?.clients?.name || 'Unknown'}</p>
        </div>
      )}
    </div>
  );
}

// Friend Card Component
function FriendCard({ friend, onChallenge }: { friend: any; onChallenge?: () => void }) {
  return (
    <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-xl p-3 hover:border-green-500/50 transition">
      <div className="flex items-center gap-2 mb-2">
        <Avatar 
          src={friend?.avatar} 
          name={friend?.name} 
          size="md" 
          />
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-sm truncate">{friend?.name || 'Unknown'}</h3>
          <p className="text-[10px] text-gray-500">Friend</p>
        </div>
      </div>
      
      <div className="flex gap-2">
        <button 
          onClick={onChallenge}
          className="flex-1 px-2 py-1.5 bg-green-500 hover:bg-green-400 text-[#0f172a] text-xs font-bold rounded-lg transition flex items-center justify-center gap-1"
        >
          <Target className="w-3 h-3" />
          Challenge
        </button>
        <button className="px-2 py-1.5 bg-[#f8fafc] hover:bg-[#f8fafc] rounded-lg transition">
          <Share2 className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

// ============================================
// CHALLENGE CATEGORIES & CREATE CHALLENGE MODAL
// ============================================

const CHALLENGE_CATEGORIES: Record<string, { label: string; types: Array<{ id: string; name: string; description: string; unit: string; icon: string; source?: string }> }> = {
  nutrition: {
    label: '🥗 Nutrition',
    types: [
      { id: 'adherence', name: 'Food Logging', description: 'Who can log meals most consistently', unit: '%', icon: '📊' },
      { id: 'calorie_target', name: 'Calorie Accuracy', description: 'Hit your daily calorie goals', unit: '%', icon: '🎯' },
      { id: 'protein_target', name: 'Protein Goals', description: 'Hit your protein targets', unit: '%', icon: '💪' },
    ]
  },
  activity: {
    label: '🏃 Activity',
    types: [
      { id: 'calories_burned', name: 'Calories Burned', description: 'Burn the most total calories', unit: 'kcal', icon: '🔥', source: 'wearable' },
      { id: 'active_calories', name: 'Active Calories', description: 'Most active calories (Garmin)', unit: 'kcal', icon: '⚡', source: 'garmin' },
      { id: 'steps', name: 'Total Steps', description: 'Who can walk the most', unit: 'steps', icon: '👟', source: 'garmin' },
      { id: 'daily_steps_goal', name: '10K Steps Challenge', description: 'Most days hitting 10,000 steps', unit: '%', icon: '🚶', source: 'garmin' },
    ]
  },
  training: {
    label: '🏋️ Training',
    types: [
      { id: 'workouts_completed', name: 'Workout Count', description: 'Complete the most workouts', unit: 'workouts', icon: '💪', source: 'wearable' },
      { id: 'workout_calories', name: 'Workout Calories', description: 'Burn most calories in workouts', unit: 'kcal', icon: '🔥', source: 'wearable' },
      { id: 'workout_minutes', name: 'Training Time', description: 'Most time spent training', unit: 'mins', icon: '⏱️', source: 'wearable' },
      { id: 'strain_score', name: 'Strain Score', description: 'Highest cumulative WHOOP strain', unit: 'strain', icon: '😤', source: 'whoop' },
      { id: 'running_distance', name: 'Running Distance', description: 'Run the furthest', unit: 'km', icon: '🏃', source: 'wearable' },
      { id: 'cycling_distance', name: 'Cycling Distance', description: 'Cycle the furthest', unit: 'km', icon: '🚴', source: 'wearable' },
      { id: 'total_distance', name: 'Total Distance', description: 'Cover the most ground', unit: 'km', icon: '📍', source: 'wearable' },
    ]
  },
  recovery: {
    label: '😴 Recovery',
    types: [
      { id: 'sleep_hours', name: 'Average Sleep', description: 'Best average sleep duration', unit: 'hrs', icon: '🛏️', source: 'wearable' },
      { id: 'sleep_consistency', name: 'Sleep Consistency', description: 'Most nights with 7+ hours', unit: '%', icon: '😴', source: 'whoop' },
      { id: 'avg_recovery', name: 'Recovery Score', description: 'Best average WHOOP recovery', unit: '%', icon: '💚', source: 'whoop' },
      { id: 'high_recovery_days', name: 'Green Recovery Days', description: 'Most days with 67%+ recovery', unit: 'days', icon: '🟢', source: 'whoop' },
      { id: 'avg_hrv', name: 'HRV Challenge', description: 'Best average HRV', unit: 'ms', icon: '❤️', source: 'whoop' },
    ]
  },
  streaks: {
    label: '🔥 Streaks',
    types: [
      { id: 'logging_streak', name: 'Logging Streak', description: 'Longest food logging streak', unit: 'days', icon: '📍' },
      { id: 'workout_streak', name: 'Workout Streak', description: 'Most consecutive workout days', unit: 'days', icon: '🏋️', source: 'wearable' },
    ]
  }
};

const ALL_CHALLENGE_TYPES = Object.values(CHALLENGE_CATEGORIES).flatMap(cat => cat.types);

function CreateChallengeModal({ onClose, onCreate, friends }: { 
  onClose: () => void; 
  onCreate: (data: any) => Promise<string>; 
  friends: any[] 
}) {
  const [step, setStep] = useState<'category' | 'type' | 'details'>('category');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    duration: '7',
    selectedFriends: [] as string[]
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedType = ALL_CHALLENGE_TYPES.find(t => t.id === formData.type);
  const categoryData = selectedCategory ? CHALLENGE_CATEGORIES[selectedCategory] : null;

  const handleCategorySelect = (categoryKey: string) => {
    setSelectedCategory(categoryKey);
    setStep('type');
  };

  const handleTypeSelect = (typeId: string) => {
    const type = ALL_CHALLENGE_TYPES.find(t => t.id === typeId);
    setFormData({
      ...formData, 
      type: typeId,
      name: type?.name || ''
    });
    setStep('details');
  };

  const handleSubmit = async () => {
    if (!formData.name.trim() || !formData.type) return;
    
    setIsSubmitting(true);
    try {
      const startDate = new Date();
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + parseInt(formData.duration));

      await onCreate({
        name: formData.name,
        type: formData.type,
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
        metric: formData.type,
        invited_friends: formData.selectedFriends
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const goBack = () => {
    if (step === 'details') setStep('type');
    else if (step === 'type') {
      setStep('category');
      setSelectedCategory(null);
    }
    else onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-end md:items-center justify-center z-50 p-0 md:p-4">
      <div className="bg-[#f8fafc] border-t md:border border-[#e2e8f0] rounded-t-2xl md:rounded-2xl p-5 w-full md:max-w-lg max-h-[85vh] overflow-y-auto safe-area-pb">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            {step !== 'category' && (
              <button onClick={goBack} className="p-1.5 hover:bg-[#f8fafc] rounded-lg transition">
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Target className="w-5 h-5 text-green-400" />
              {step === 'category' ? 'Create Challenge' : 
               step === 'type' ? categoryData?.label : 
               'Details'}
            </h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#f8fafc] rounded-lg transition">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Step 1: Select Category */}
        {step === 'category' && (
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(CHALLENGE_CATEGORIES).map(([key, category]) => (
              <button
                key={key}
                onClick={() => handleCategorySelect(key)}
                className="p-3 bg-[#f8fafc] hover:bg-[#f1f5f9] border border-[#e2e8f0] hover:border-green-500/50 rounded-xl text-left transition"
              >
                <span className="text-xl mb-1 block">{category.label.split(' ')[0]}</span>
                <span className="font-medium text-[#0f172a] text-sm">{category.label.split(' ').slice(1).join(' ')}</span>
                <p className="text-[10px] text-gray-500 mt-0.5">{category.types.length} types</p>
              </button>
            ))}
          </div>
        )}

        {/* Step 2: Select Type */}
        {step === 'type' && categoryData && (
          <div className="space-y-2">
            {categoryData.types.map(type => (
              <button
                key={type.id}
                onClick={() => handleTypeSelect(type.id)}
                className="w-full p-3 bg-[#f8fafc] hover:bg-[#f1f5f9] border border-[#e2e8f0] hover:border-green-500/50 rounded-xl text-left transition flex items-start gap-2"
              >
                <span className="text-xl">{type.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-medium text-[#0f172a] text-sm">{type.name}</span>
                    {type.source && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                        type.source === 'garmin' ? 'bg-blue-500/20 text-blue-400' :
                        type.source === 'whoop' ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-purple-500/20 text-purple-400'
                      }`}>
                        {type.source === 'wearable' ? 'âŒš' : 
                         type.source === 'garmin' ? 'Garmin' : 
                         'WHOOP'}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5 truncate">{type.description}</p>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Step 3: Details */}
        {step === 'details' && (
          <div className="space-y-4">
            {/* Selected Type Preview */}
            {selectedType && (
              <div className="p-3 bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-xl">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{selectedType.icon}</span>
                  <div>
                    <p className="font-bold text-[#0f172a] text-sm">{selectedType.name}</p>
                    <p className="text-xs text-gray-500">{selectedType.description}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Challenge Name */}
            <div>
              <label className="block text-xs font-medium mb-1.5">Challenge Name</label>
              <input
                type="text"
                placeholder="e.g., Weekly Step Showdown"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-3 py-2 bg-[#f8fafc] border border-gray-700 rounded-lg focus:border-green-500 outline-none text-sm"
              />
            </div>

            {/* Duration */}
            <div>
              <label className="block text-xs font-medium mb-1.5">Duration</label>
              <div className="grid grid-cols-4 gap-2">
                {['7', '14', '21', '30'].map(days => (
                  <button
                    key={days}
                    type="button"
                    onClick={() => setFormData({...formData, duration: days})}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition ${
                      formData.duration === days
                        ? 'bg-green-500 text-[#0f172a]'
                        : 'bg-[#f8fafc] text-gray-500 hover:bg-[#f8fafc]'
                    }`}
                  >
                    {days}d
                  </button>
                ))}
              </div>
            </div>

            {/* Invite Friends */}
            {friends.length > 0 && (
              <div>
                <label className="block text-xs font-medium mb-1.5">Invite Friends</label>
                <div className="max-h-32 overflow-y-auto space-y-1.5">
                  {friends.map(friendship => (
                    <label key={friendship.id} className="flex items-center gap-2 p-2 bg-[#f8fafc] rounded-lg cursor-pointer hover:bg-[#f8fafc]">
                      <input 
                        type="checkbox"
                        checked={formData.selectedFriends.includes(friendship.friend?.user_id)}
                        onChange={(e) => {
                          const friendId = friendship.friend?.user_id;
                          if (e.target.checked) {
                            setFormData({...formData, selectedFriends: [...formData.selectedFriends, friendId]});
                          } else {
                            setFormData({...formData, selectedFriends: formData.selectedFriends.filter(id => id !== friendId)});
                          }
                        }}
                        className="w-4 h-4 accent-green-500" 
                      />
                      <Avatar src={friendship.friend?.avatar} name={friendship.friend?.name} size="sm" />
                      <span className="text-xs">{friendship.friend?.name || 'Unknown'}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Submit */}
            <div className="flex gap-2 pt-2">
              <button
                onClick={goBack}
                className="flex-1 px-4 py-2 bg-[#f8fafc] hover:bg-[#f8fafc] rounded-lg font-medium transition text-sm"
              >
                Back
              </button>
              <button
                onClick={handleSubmit}
                disabled={!formData.name.trim() || isSubmitting}
                className="flex-1 px-4 py-2.5 bg-green-500 hover:bg-green-400 disabled:opacity-50 disabled:cursor-not-allowed text-[#0f172a] font-bold rounded-lg transition flex items-center justify-center gap-2 text-sm"
              >
                {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                Create
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Create Custom Leaderboard Modal
function CreateCustomLeaderboardModal({ onClose, onCreate, friends }: { onClose: () => void; onCreate: (name: string, description: string, memberIds: string[]) => Promise<void>; friends: any[] }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedFriends, setSelectedFriends] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCreate = async () => {
    if (!name.trim() || selectedFriends.length === 0) return;
    
    setIsSubmitting(true);
    try {
      await onCreate(name, description, selectedFriends);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-end md:items-center justify-center z-50 p-0 md:p-4">
      <div className="bg-[#f8fafc] border-t md:border border-[#e2e8f0] rounded-t-2xl md:rounded-2xl p-5 w-full md:max-w-md max-h-[85vh] overflow-y-auto safe-area-pb">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Users className="w-5 h-5 text-green-400" />
            Create Squad
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-[#f8fafc] rounded-lg transition">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium mb-1.5">Squad Name</label>
            <input
              type="text"
              placeholder="e.g., Morning Warriors"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 bg-[#f8fafc] border border-gray-700 rounded-lg focus:border-green-500 outline-none text-sm"
            />
          </div>

          <div>
            <label className="block text-xs font-medium mb-1.5">Description</label>
            <textarea
              placeholder="What's this squad about?"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 bg-[#f8fafc] border border-gray-700 rounded-lg focus:border-green-500 outline-none resize-none text-sm"
            />
          </div>

          <div>
            <label className="block text-xs font-medium mb-1.5">Add Members</label>
            {friends.length > 0 ? (
              <div className="max-h-32 overflow-y-auto space-y-1.5">
                {friends.map(friendship => (
                  <label key={friendship.id} className="flex items-center gap-2 p-2 bg-[#f8fafc] rounded-lg cursor-pointer hover:bg-[#f8fafc]">
                    <input
                      type="checkbox"
                      checked={selectedFriends.includes(friendship.friend?.user_id)}
                      onChange={(e) => {
                        const friendId = friendship.friend?.user_id;
                        if (e.target.checked) {
                          setSelectedFriends([...selectedFriends, friendId]);
                        } else {
                          setSelectedFriends(selectedFriends.filter(id => id !== friendId));
                        }
                      }}
                      className="w-4 h-4 accent-green-500"
                    />
                    <Avatar src={friendship.friend?.avatar} name={friendship.friend?.name} size="sm" />
                    <span className="text-xs">{friendship.friend?.name || 'Unknown'}</span>
                  </label>
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-500 py-3 text-center">Add friends first to create a squad</p>
            )}
          </div>

          <div className="flex gap-2 pt-2">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-[#f8fafc] hover:bg-[#f8fafc] rounded-lg font-medium transition text-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={!name.trim() || selectedFriends.length === 0 || isSubmitting}
              className="flex-1 px-4 py-2.5 bg-green-500 hover:bg-green-400 disabled:opacity-50 disabled:cursor-not-allowed text-[#0f172a] font-bold rounded-lg transition flex items-center justify-center gap-2 text-sm"
            >
              {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Create
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Invite Friends Modal
function InviteFriendsModal({ onClose }: { onClose: () => void }) {
  const [inviteLink, setInviteLink] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const generateInviteLink = async () => {
      const user = await currentUser();
      if (user) {
        const link = `${window.location.origin}/join/${user.id}`;
        setInviteLink(link);
      }
    };
    generateInviteLink();
  }, []);

  const copyLink = () => {
    navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-end md:items-center justify-center z-50 p-0 md:p-4">
      <div className="bg-[#f8fafc] border-t md:border border-[#e2e8f0] rounded-t-2xl md:rounded-2xl p-5 w-full md:max-w-md safe-area-pb">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-green-400" />
            Invite Friends
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-[#f8fafc] rounded-lg transition">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <p className="text-gray-500 text-sm">
            Share this link with friends to compete together!
          </p>

          <div className="flex gap-2">
            <input
              type="text"
              value={inviteLink}
              readOnly
              className="flex-1 px-3 py-2 bg-[#f8fafc] border border-gray-700 rounded-lg text-xs truncate"
            />
            <button
              onClick={copyLink}
              className={`px-4 py-2 font-bold rounded-lg transition flex items-center gap-1.5 text-sm ${
                copied 
                  ? 'bg-green-600 text-[#0f172a]' 
                  : 'bg-green-500 hover:bg-green-400 text-[#0f172a]'
              }`}
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>

          <div className="pt-3 border-t border-gray-800">
            <p className="text-xs text-gray-500 mb-2">Or share via:</p>
            <button 
              onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`Join me on Input! ${inviteLink}`)}`, '_blank')}
              className="w-full px-4 py-2.5 bg-[#25D366] hover:bg-[#20BD5C] text-[#0f172a] rounded-lg font-medium transition text-sm"
            >
              WhatsApp
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
